import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, EyeOff, Music2, X } from "lucide-react";
import { toast } from "sonner";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeading,
  StatusBadge,
} from "@/components/app/AppUI";
import { eventRepository, songRepository } from "@/data/repositories";
import { SONG_CATEGORY_LABELS } from "@/domain/songs";
import type { ModerationStatus } from "@/domain/types";

export const Route = createFileRoute("/_authenticated/app/canciones")({ component: MusicPage });

function MusicPage() {
  const client = useQueryClient();
  const songs = useQuery({ queryKey: ["songs"], queryFn: () => songRepository.list() });
  const events = useQuery({ queryKey: ["events"], queryFn: () => eventRepository.list() });
  const eventNames = new Map((events.data ?? []).map((event) => [event.id, event.title]));
  const moderate = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ModerationStatus }) =>
      songRepository.moderate(id, status),
    onSuccess: () => {
      toast.success("Moderación actualizada");
      client.invalidateQueries({ queryKey: ["songs"] });
    },
    onError: (error) => toast.error(error.message),
  });
  if (songs.isLoading || events.isLoading) return <LoadingState />;
  if (songs.error || events.error)
    return <ErrorState message={songs.error?.message ?? events.error?.message ?? "Error"} />;
  const pending = (songs.data ?? []).filter((song) => song.moderation === "PENDING");
  const approved = (songs.data ?? []).filter((song) => song.moderation !== "PENDING");
  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Selección"
        title="Música"
        description="Preferencias de clientes y propuestas de invitados. Nada se publica sin aprobación."
      />
      <section className="panel border-t-4 !border-t-primary">
        <div className="panel-header">
          <div>
            <p className="eyebrow text-primary">Bandeja de moderación</p>
            <h2 className="mt-1 font-display text-xl">{pending.length} propuestas pendientes</h2>
          </div>
          <Music2 className="size-5 text-primary" />
        </div>
        {pending.length === 0 ? (
          <EmptyState
            title="Moderación al día"
            description="Las nuevas propuestas QR aparecerán aquí."
          />
        ) : (
          pending.map((song) => (
            <div
              key={song.id}
              className="data-row grid-cols-[1fr_auto] md:grid-cols-[1fr_1fr_auto]"
            >
              <div>
                <p className="font-semibold">{song.title}</p>
                <p className="text-sm text-muted-foreground">
                  {song.artist || "Artista sin indicar"} · por {song.guest_alias || "Invitado"}
                </p>
              </div>
              <Link
                to="/app/eventos/$id"
                params={{ id: song.event_id }}
                className="hidden text-sm md:block"
              >
                {eventNames.get(song.event_id) ?? "Evento"}
              </Link>
              <div className="flex gap-2">
                <button
                  className="btn-primary !min-h-9 !px-3"
                  onClick={() => moderate.mutate({ id: song.id, status: "APPROVED" })}
                >
                  <Check className="size-4" /> Aprobar
                </button>
                <button
                  className="btn-danger !min-h-9 !px-3"
                  onClick={() => moderate.mutate({ id: song.id, status: "REJECTED" })}
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </section>
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">Catálogo</p>
            <h2 className="mt-1 font-display text-xl">Canciones gestionadas</h2>
          </div>
        </div>
        {approved.length === 0 ? (
          <EmptyState title="Sin canciones" description="Añádelas desde el detalle de un evento." />
        ) : (
          approved.map((song) => (
            <div
              key={song.id}
              className="data-row grid-cols-[1fr_auto] md:grid-cols-[1fr_1fr_8rem_auto]"
            >
              <div>
                <p className="font-semibold">{song.title}</p>
                <p className="text-sm text-muted-foreground">{song.artist || "—"}</p>
              </div>
              <Link
                to="/app/eventos/$id"
                params={{ id: song.event_id }}
                className="hidden md:block"
              >
                {eventNames.get(song.event_id) ?? "Evento"}
              </Link>
              <span className="hidden text-sm md:block">{SONG_CATEGORY_LABELS[song.category]}</span>
              <div className="flex items-center gap-3">
                <StatusBadge status={song.moderation} />
                {song.moderation === "APPROVED" && (
                  <button
                    title="Ocultar"
                    onClick={() => moderate.mutate({ id: song.id, status: "HIDDEN" })}
                  >
                    <EyeOff className="size-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
